import React from 'react';
import { Play } from 'lucide-react';
import { TOKENS } from '../styles';
import type { PD7AStatus } from '../types';

interface Props {
  dueDate: string;
  amount: number;
  status: PD7AStatus;
  periodLabel: string;
  onPayFile: () => void;
}

function computeHeadline(dueDate: string): { headline: string; isOverdue: boolean } {
  if (!dueDate) return { headline: 'Next remittance', isOverdue: false };
  const parts = dueDate.split('/');
  if (parts.length !== 3) return { headline: 'Next remittance', isOverdue: false };

  const dueDateObj = new Date(
    parseInt(parts[2], 10),
    parseInt(parts[1], 10) - 1,
    parseInt(parts[0], 10),
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = dueDateObj.getTime() - today.getTime();
  const daysUntil = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (daysUntil < 0) {
    const n = Math.abs(daysUntil);
    return { headline: n + ' day' + (n === 1 ? '' : 's') + ' overdue', isOverdue: true };
  }
  if (daysUntil === 0) return { headline: 'Due today', isOverdue: false };
  if (daysUntil === 1) return { headline: '1 day until due', isOverdue: false };
  return { headline: daysUntil + ' days until due', isOverdue: false };
}

export default function HeroCard({ dueDate, amount, periodLabel, onPayFile }: Props) {
  const { headline, isOverdue } = computeHeadline(dueDate);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #EAF8F4, #F1F8F6)',
      border: '1px solid #D5EDE6',
      borderRadius: 18,
      padding: '28px 30px',
      marginBottom: 22,
      marginTop: 18,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: TOKENS.tealInk, marginBottom: 8,
      }}>
        Next remittance
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', gap: 24, flexWrap: 'wrap',
      }}>
        <div>
          <div style={{
            fontSize: 34, fontWeight: 600,
            color: isOverdue ? '#C5483B' : TOKENS.ink,
            letterSpacing: '-0.02em', lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {headline}
          </div>
          {dueDate && (
            <div style={{ fontSize: 14, color: TOKENS.dark, marginTop: 6, fontWeight: 500 }}>
              Due <b style={{ color: TOKENS.ink, fontWeight: 600 }}>{dueDate}</b>
              {periodLabel && (
                <> &middot; Period <b style={{ color: TOKENS.ink, fontWeight: 600 }}>{periodLabel}</b></>
              )}
            </div>
          )}
          <div style={{ fontSize: 14, color: TOKENS.dark, marginTop: 4, fontWeight: 500 }}>
            Amount <b style={{ color: TOKENS.ink, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>${amount.toFixed(2)}</b>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <button
            onClick={onPayFile}
            style={{
              background: TOKENS.teal, color: '#fff',
              padding: '12px 22px', border: 'none', borderRadius: 11,
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 9,
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(21,160,140,0.28)',
            }}
          >
            <Play size={17} /> Pay and file
          </button>
        </div>
      </div>
    </div>
  );
}